import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import AdminLayout from '../components/AdminLayout';
import { showtimesAPI, moviesAPI, branchesAPI, showtimesImportAPI } from '../api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Trash2, Plus, Calendar, Edit2, Filter, X } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminShowtimes = () => {
  const [showtimes, setShowtimes] = useState([]);
  const [filteredShowtimes, setFilteredShowtimes] = useState([]);
  const [movies, setMovies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedShowtimes, setSelectedShowtimes] = useState([]);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [movieMappings, setMovieMappings] = useState({});
  const [branchMappings, setBranchMappings] = useState({});
  const { toast } = useToast();

  // --- Yeni çok-lokasyonlu form state ---
  const [commonData, setCommonData] = useState({
    movie_id: '',
    start_date: '',
    end_date: '',
  });

  // Her branch için bir satır: { branch_id, enabled, hall_number, format, language, show_times }
  const [branchRows, setBranchRows] = useState([]);

  const [filters, setFilters] = useState({
    movie_id: '',
    branch_id: '',
    hall_number: '',
    start_date: '',
    end_date: '',
    format: '',
    language: '',
    show_past: false,
  });

  const [bulkEditData, setBulkEditData] = useState({
    branch_id: '',
    hall_number: '',
    show_time: '',
    format: '',
    language: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, showtimes]);

  const loadData = async () => {
    try {
      const [showtimesRes, moviesRes, branchesRes] = await Promise.all([
        showtimesAPI.getAll(),
        moviesAPI.getAll(),
        branchesAPI.getAll(),
      ]);

      const activeMovies = moviesRes.data.filter(m => m.status !== 'archived');
      const today = new Date().toISOString().split('T')[0];
      const futureShowtimes = showtimesRes.data.filter(s => s.show_date >= today);

      setShowtimes(showtimesRes.data);
      setFilteredShowtimes(futureShowtimes);
      setMovies(activeMovies);
      setBranches(branchesRes.data);

      // Branch rows'u yükle (form henüz açık değilse sadece veriyi hazırla)
      const rows = branchesRes.data.map(branch => ({
        branch_id: branch._id,
        enabled: true,
        hall_number: 'Salon 1',
        format: '2D',
        language: 'Türkçe Dublaj',
        show_times: '',
      }));
      setBranchRows(rows);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Hata',
        description: 'Veriler yüklenirken bir hata oluştu.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...showtimes];

    if (!filters.show_past) {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(s => s.show_date >= today);
    }
    if (filters.movie_id) filtered = filtered.filter(s => s.movie_id === filters.movie_id);
    if (filters.branch_id) filtered = filtered.filter(s => s.branch_id === filters.branch_id);
    if (filters.hall_number) filtered = filtered.filter(s => s.hall_number === filters.hall_number);
    if (filters.format) filtered = filtered.filter(s => s.format === filters.format);
    if (filters.language) filtered = filtered.filter(s => s.language === filters.language);
    if (filters.start_date) filtered = filtered.filter(s => s.show_date >= filters.start_date);
    if (filters.end_date) filtered = filtered.filter(s => s.show_date <= filters.end_date);

    setFilteredShowtimes(filtered);
    setSelectedShowtimes([]);
  };

  const clearFilters = () => {
    setFilters({
      movie_id: '',
      branch_id: '',
      hall_number: '',
      start_date: '',
      end_date: '',
      format: '',
      language: '',
      show_past: false,
    });
  };

  const updateBranchRow = (branchId, field, value) => {
    setBranchRows(prev =>
      prev.map(row => row.branch_id === branchId ? { ...row, [field]: value } : row)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const activeRows = branchRows.filter(row => row.enabled);

    if (activeRows.length === 0) {
      toast({ title: 'Hata', description: 'En az bir lokasyon seçmelisiniz.', variant: 'destructive' });
      return;
    }

    const errors = [];
    activeRows.forEach(row => {
      const times = row.show_times.split(',').map(t => t.trim()).filter(t => t);
      if (times.length === 0) {
        const branchName = branches.find(b => b._id === row.branch_id)?.name || row.branch_id;
        errors.push(`${branchName}: Seans saati girilmemiş`);
      }
    });

    if (errors.length > 0) {
      toast({ title: 'Hata', description: errors.join(' | '), variant: 'destructive' });
      return;
    }

    try {
      const results = await Promise.allSettled(
        activeRows.map(row => {
          const times = row.show_times.split(',').map(t => t.trim()).filter(t => t);
          return showtimesAPI.createBulk({
            movie_id: commonData.movie_id,
            branch_id: row.branch_id,
            hall_number: row.hall_number,
            start_date: commonData.start_date,
            end_date: commonData.end_date,
            show_times: times,
            format: row.format,
            language: row.language,
          });
        })
      );

      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed === 0) {
        toast({ title: 'Başarılı', description: `${succeeded} lokasyon için seanslar eklendi.` });
      } else {
        toast({
          title: 'Kısmen Başarılı',
          description: `${succeeded} lokasyon eklendi, ${failed} lokasyonda hata oluştu.`,
          variant: 'destructive',
        });
      }

      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving showtimes:', error);
      toast({ title: 'Hata', description: 'Seanslar kaydedilirken bir hata oluştu.', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu seansı silmek istediğinizden emin misiniz?')) return;
    try {
      await showtimesAPI.delete(id);
      toast({ title: 'Başarılı', description: 'Seans silindi.' });
      loadData();
    } catch (error) {
      toast({ title: 'Hata', description: 'Seans silinirken bir hata oluştu.', variant: 'destructive' });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedShowtimes.length === 0) {
      toast({ title: 'Uyarı', description: 'Lütfen silmek için en az bir seans seçin.', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`${selectedShowtimes.length} seansı silmek istediğinizden emin misiniz?`)) return;
    try {
      const response = await showtimesAPI.bulkDelete(selectedShowtimes);
      toast({ title: 'Başarılı', description: `${response.data.deleted_count} seans silindi.` });
      setSelectedShowtimes([]);
      loadData();
    } catch (error) {
      toast({ title: 'Hata', description: 'Seanslar silinirken bir hata oluştu.', variant: 'destructive' });
    }
  };

  const handleImportFile = (event) => {
    const file = event.target.files[0];
    if (file) setImportFile(file);
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      toast({ title: 'Uyarı', description: 'Lütfen bir dosya seçin.', variant: 'destructive' });
      return;
    }
    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Content = e.target.result.split(',')[1];
          const validation = await showtimesImportAPI.validateImport(base64Content);
          if (!validation.data.valid) {
            setValidationResult({ ...validation.data, base64Content });
            setMovieMappings({});
            setBranchMappings({});
            setShowImportModal(false);
            setShowMappingModal(true);
            setImporting(false);
            return;
          }
          const response = await showtimesImportAPI.importFromFile(base64Content);
          toast({ title: 'Başarılı!', description: response.data.message });
          setShowImportModal(false);
          setImportFile(null);
          loadData();
        } catch (error) {
          toast({ title: 'Hata', description: error.response?.data?.detail || 'Dosya yüklenirken hata oluştu', variant: 'destructive' });
        } finally {
          setImporting(false);
        }
      };
      reader.readAsDataURL(importFile);
    } catch (error) {
      toast({ title: 'Hata', description: 'Dosya okunamadı', variant: 'destructive' });
      setImporting(false);
    }
  };

  const handleConfirmMapping = async () => {
    if (!validationResult) return;
    setImporting(true);
    try {
      const response = await showtimesImportAPI.importFromFile(validationResult.base64Content, movieMappings, branchMappings);
      toast({ title: 'Başarılı!', description: response.data.message });
      setShowMappingModal(false);
      setValidationResult(null);
      setImportFile(null);
      loadData();
    } catch (error) {
      toast({ title: 'Hata', description: error.response?.data?.detail || 'Import sırasında hata oluştu', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  const handleBulkEdit = async () => {
    if (selectedShowtimes.length === 0) {
      toast({ title: 'Uyarı', description: 'Lütfen düzenlemek için en az bir seans seçin.', variant: 'destructive' });
      return;
    }
    setShowBulkEditModal(true);
  };

  const submitBulkEdit = async (e) => {
    e.preventDefault();
    const updates = {};
    if (bulkEditData.branch_id) updates.branch_id = bulkEditData.branch_id;
    if (bulkEditData.show_time) updates.show_time = bulkEditData.show_time;
    if (bulkEditData.format) updates.format = bulkEditData.format;
    if (bulkEditData.language) updates.language = bulkEditData.language;
    if (bulkEditData.hall_number) updates.hall_number = bulkEditData.hall_number;

    if (Object.keys(updates).length === 0) {
      toast({ title: 'Uyarı', description: 'Lütfen en az bir alan güncelleyin.', variant: 'destructive' });
      return;
    }
    try {
      const response = await showtimesAPI.bulkUpdate({ showtime_ids: selectedShowtimes, updates });
      toast({ title: 'Başarılı', description: `${response.data.updated_count} seans güncellendi.` });
      setShowBulkEditModal(false);
      setBulkEditData({ branch_id: '', hall_number: '', show_time: '', format: '', language: '' });
      setSelectedShowtimes([]);
      loadData();
    } catch (error) {
      toast({ title: 'Hata', description: 'Seanslar güncellenirken bir hata oluştu.', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setCommonData({ movie_id: '', start_date: '', end_date: '' });
    setBranchRows(branches.map(branch => ({
      branch_id: branch._id,
      enabled: true,
      hall_number: 'Salon 1',
      format: '2D',
      language: 'Türkçe Dublaj',
      show_times: '',
    })));
    setShowForm(false);
  };

  const toggleSelectAll = () => {
    if (selectedShowtimes.length === filteredShowtimes.length) {
      setSelectedShowtimes([]);
    } else {
      setSelectedShowtimes(filteredShowtimes.map(s => s._id));
    }
  };

  const toggleSelectShowtime = (id) => {
    if (selectedShowtimes.includes(id)) {
      setSelectedShowtimes(selectedShowtimes.filter(sid => sid !== id));
    } else {
      setSelectedShowtimes([...selectedShowtimes, id]);
    }
  };

  const groupedShowtimes = filteredShowtimes.reduce((acc, showtime) => {
    const movieId = showtime.movie_id;
    const branchId = showtime.branch_id;
    const date = showtime.show_date;
    if (!acc[movieId]) acc[movieId] = {};
    if (!acc[movieId][branchId]) acc[movieId][branchId] = {};
    if (!acc[movieId][branchId][date]) acc[movieId][branchId][date] = [];
    acc[movieId][branchId][date].push(showtime);
    return acc;
  }, {});

  const getMovieName = (movieId) => {
    const movie = movies.find(m => m._id === movieId);
    return movie ? movie.title : 'Bilinmeyen Film';
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b._id === branchId);
    return branch ? branch.name : 'Bilinmeyen Salon';
  };

  const hasActiveFilters = filters.movie_id || filters.branch_id || filters.hall_number || filters.start_date || filters.end_date || filters.format || filters.language || filters.show_past;

  return (
    <AdminLayout>
      <Helmet>
        <title>Seans Yönetimi - Cinefora Admin</title>
      </Helmet>

      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-3xl font-bold text-white">Seans Yönetimi</h1>
          <div className="flex gap-2">
            <Button onClick={() => setShowImportModal(true)} className="bg-green-600 hover:bg-green-700 text-white">
              📊 Excel'den İçe Aktar
            </Button>
            <Button
              onClick={() => setShowForm(!showForm)}
              className="bg-[#FDB714] hover:bg-[#e5a510] text-black"
            >
              <Plus className="mr-2" size={20} />
              Toplu Seans Ekle
            </Button>
          </div>
        </div>

        {selectedShowtimes.length > 0 && (
          <div className="bg-blue-900/50 border border-blue-700 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-white font-medium">{selectedShowtimes.length} seans seçildi</span>
              <div className="flex gap-2">
                <Button onClick={handleBulkEdit} className="bg-yellow-600 hover:bg-yellow-700 text-white">
                  <Edit2 className="mr-2" size={18} /> Toplu Düzenle
                </Button>
                <Button onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700 text-white">
                  <Trash2 className="mr-2" size={18} /> Toplu Sil
                </Button>
                <Button onClick={() => setSelectedShowtimes([])} className="bg-gray-600 hover:bg-gray-700 text-white">
                  <X className="mr-2" size={18} /> Seçimi Temizle
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-gray-900 border-gray-800 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white flex items-center">
            <Filter className="mr-2" size={20} /> Filtrele
          </h2>
          {hasActiveFilters && (
            <Button onClick={clearFilters} className="bg-gray-700 hover:bg-gray-600 text-white text-sm">
              <X className="mr-1" size={16} /> Filtreleri Temizle
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-gray-300 block mb-2 text-sm">Film</label>
            <select value={filters.movie_id} onChange={(e) => setFilters({ ...filters, movie_id: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="">Tüm Filmler</option>
              {movies.map((movie) => <option key={movie._id} value={movie._id}>{movie.title}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-300 block mb-2 text-sm">Lokasyon</label>
            <select value={filters.branch_id} onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="">Tüm Lokasyonlar</option>
              {branches.map((branch) => <option key={branch._id} value={branch._id}>{branch.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-300 block mb-2 text-sm">Salon No</label>
            <input type="text" value={filters.hall_number} onChange={(e) => setFilters({ ...filters, hall_number: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" placeholder="Salon 1, VIP..." />
          </div>
          <div>
            <label className="text-gray-300 block mb-2 text-sm">Başlangıç Tarihi</label>
            <input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <div>
            <label className="text-gray-300 block mb-2 text-sm">Bitiş Tarihi</label>
            <input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" />
          </div>
          <div>
            <label className="text-gray-300 block mb-2 text-sm">Format</label>
            <select value={filters.format} onChange={(e) => setFilters({ ...filters, format: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="">Tüm Formatlar</option>
              <option value="2D">2D</option>
              <option value="3D">3D</option>
              <option value="IMAX">IMAX</option>
            </select>
          </div>
          <div>
            <label className="text-gray-300 block mb-2 text-sm">Dil</label>
            <select value={filters.language} onChange={(e) => setFilters({ ...filters, language: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm">
              <option value="">Tüm Diller</option>
              <option value="Türkçe Dublaj">Türkçe Dublaj</option>
              <option value="Türkçe Altyazı">Türkçe Altyazı</option>
              <option value="Türkçe">Türkçe</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Toplam {filteredShowtimes.length} seans gösteriliyor
            {hasActiveFilters && ` (${showtimes.length} seansdan filtrelendi)`}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={filters.show_past} onChange={(e) => setFilters({ ...filters, show_past: e.target.checked })} className="w-4 h-4 cursor-pointer" />
            <span>Geçmiş seansları da göster</span>
          </label>
        </div>
      </Card>

      {/* Çok Lokasyonlu Toplu Seans Ekleme Formu */}
      {showForm && (
        <Card className="bg-gray-900 border-gray-800 p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-6">Toplu Seans Ekle</h2>
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Ortak alanlar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-gray-300 block mb-2">Film</label>
                <select
                  value={commonData.movie_id}
                  onChange={(e) => setCommonData({ ...commonData, movie_id: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  required
                >
                  <option value="">Film Seçin</option>
                  {movies.map((movie) => <option key={movie._id} value={movie._id}>{movie.title}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Başlangıç Tarihi</label>
                <input
                  type="date"
                  value={commonData.start_date}
                  onChange={(e) => setCommonData({ ...commonData, start_date: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Bitiş Tarihi</label>
                <input
                  type="date"
                  value={commonData.end_date}
                  onChange={(e) => setCommonData({ ...commonData, end_date: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  required
                />
              </div>
            </div>

            {/* Lokasyon satırları */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-gray-300 font-medium">Lokasyonlar</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setBranchRows(prev => prev.map(r => ({ ...r, enabled: true })))}
                    className="text-xs text-blue-400 hover:text-blue-300 underline"
                  >
                    Tümünü Seç
                  </button>
                  <span className="text-gray-600">|</span>
                  <button
                    type="button"
                    onClick={() => setBranchRows(prev => prev.map(r => ({ ...r, enabled: false })))}
                    className="text-xs text-gray-400 hover:text-gray-300 underline"
                  >
                    Tümünü Kaldır
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                {/* Başlık satırı */}
                <div className="hidden md:grid md:grid-cols-[24px_1fr_120px_100px_160px_1fr] gap-3 px-3 pb-1">
                  <div></div>
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Lokasyon</span>
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Salon No</span>
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Format</span>
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Dil</span>
                  <span className="text-gray-500 text-xs uppercase tracking-wide">Saatler (virgülle)</span>
                </div>

                {branchRows.map((row) => {
                  const branch = branches.find(b => b._id === row.branch_id);
                  return (
                    <div
                      key={row.branch_id}
                      className={`rounded-lg border transition-colors ${
                        row.enabled
                          ? 'bg-gray-800 border-gray-700'
                          : 'bg-gray-900 border-gray-800 opacity-50'
                      }`}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-[24px_1fr_120px_100px_160px_1fr] gap-3 p-3 items-center">
                        {/* Checkbox */}
                        <input
                          type="checkbox"
                          checked={row.enabled}
                          onChange={(e) => updateBranchRow(row.branch_id, 'enabled', e.target.checked)}
                          className="w-4 h-4 cursor-pointer accent-yellow-500"
                        />

                        {/* Lokasyon adı */}
                        <span className={`font-medium text-sm ${row.enabled ? 'text-white' : 'text-gray-500'}`}>
                          {branch?.name || 'Bilinmeyen'}
                        </span>

                        {/* Salon No */}
                        <input
                          type="text"
                          value={row.hall_number}
                          onChange={(e) => updateBranchRow(row.branch_id, 'hall_number', e.target.value)}
                          disabled={!row.enabled}
                          className="bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm disabled:opacity-40 w-full"
                          placeholder="Salon 1"
                        />

                        {/* Format */}
                        <select
                          value={row.format}
                          onChange={(e) => updateBranchRow(row.branch_id, 'format', e.target.value)}
                          disabled={!row.enabled}
                          className="bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm disabled:opacity-40 w-full"
                        >
                          <option value="2D">2D</option>
                          <option value="3D">3D</option>
                          <option value="IMAX">IMAX</option>
                        </select>

                        {/* Dil */}
                        <select
                          value={row.language}
                          onChange={(e) => updateBranchRow(row.branch_id, 'language', e.target.value)}
                          disabled={!row.enabled}
                          className="bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm disabled:opacity-40 w-full"
                        >
                          <option value="Türkçe Dublaj">Türkçe Dublaj</option>
                          <option value="Türkçe Altyazı">Türkçe Altyazı</option>
                          <option value="Türkçe">Türkçe</option>
                        </select>

                        {/* Saatler */}
                        <input
                          type="text"
                          value={row.show_times}
                          onChange={(e) => updateBranchRow(row.branch_id, 'show_times', e.target.value)}
                          disabled={!row.enabled}
                          className="bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm disabled:opacity-40 w-full"
                          placeholder="11:00, 14:00, 17:00"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-gray-500 text-xs mt-2">
                Aktif lokasyon sayısı: {branchRows.filter(r => r.enabled).length} / {branchRows.length}
              </p>
            </div>

            <div className="flex gap-3">
              <Button type="submit" className="bg-[#FDB714] hover:bg-[#e5a510] text-black">
                <Calendar className="mr-2" size={18} />
                Seansları Ekle
              </Button>
              <Button type="button" onClick={resetForm} className="bg-gray-700 hover:bg-gray-600 text-white">
                İptal
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-900 border-gray-800 p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">
              Toplu Düzenleme ({selectedShowtimes.length} seans)
            </h2>
            <form onSubmit={submitBulkEdit} className="space-y-4">
              <div>
                <label className="text-gray-300 block mb-2">Lokasyon (İsteğe Bağlı)</label>
                <select value={bulkEditData.branch_id} onChange={(e) => setBulkEditData({ ...bulkEditData, branch_id: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
                  <option value="">Değiştirme</option>
                  {branches.map((branch) => <option key={branch._id} value={branch._id}>{branch.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Salon Numarası (İsteğe Bağlı)</label>
                <input type="text" value={bulkEditData.hall_number} onChange={(e) => setBulkEditData({ ...bulkEditData, hall_number: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white" placeholder="Örn: Salon 1, VIP Salon" />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Seans Saati (İsteğe Bağlı)</label>
                <input type="time" value={bulkEditData.show_time} onChange={(e) => setBulkEditData({ ...bulkEditData, show_time: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white" />
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Format (İsteğe Bağlı)</label>
                <select value={bulkEditData.format} onChange={(e) => setBulkEditData({ ...bulkEditData, format: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
                  <option value="">Değiştirme</option>
                  <option value="2D">2D</option>
                  <option value="3D">3D</option>
                  <option value="IMAX">IMAX</option>
                </select>
              </div>
              <div>
                <label className="text-gray-300 block mb-2">Dil (İsteğe Bağlı)</label>
                <select value={bulkEditData.language} onChange={(e) => setBulkEditData({ ...bulkEditData, language: e.target.value })} className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white">
                  <option value="">Değiştirme</option>
                  <option value="Türkçe Dublaj">Türkçe Dublaj</option>
                  <option value="Türkçe Altyazı">Türkçe Altyazı</option>
                  <option value="Türkçe">Türkçe</option>
                </select>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-[#FDB714] hover:bg-[#e5a510] text-black flex-1">Güncelle</Button>
                <Button type="button" onClick={() => { setShowBulkEditModal(false); setBulkEditData({ branch_id: '', hall_number: '', show_time: '', format: '', language: '' }); }} className="bg-gray-700 hover:bg-gray-600 text-white flex-1">İptal</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Showtimes List */}
      {loading ? (
        <p className="text-white text-center">Yükleniyor...</p>
      ) : Object.keys(groupedShowtimes).length === 0 ? (
        <Card className="bg-gray-900 border-gray-800 p-8 text-center">
          <p className="text-gray-400">
            {hasActiveFilters ? 'Filtre kriterlerinize uygun seans bulunamadı.' : 'Henüz seans eklenmemiş.'}
          </p>
        </Card>
      ) : (
        <>
          <div className="mb-4">
            <label className="flex items-center text-white cursor-pointer">
              <input type="checkbox" checked={selectedShowtimes.length === filteredShowtimes.length && filteredShowtimes.length > 0} onChange={toggleSelectAll} className="mr-2 w-5 h-5 cursor-pointer" />
              <span className="font-medium">Tümünü Seç ({filteredShowtimes.length} seans)</span>
            </label>
          </div>

          <div className="space-y-6">
            {Object.entries(groupedShowtimes).map(([movieId, branchesData]) => (
              <Card key={movieId} className="bg-gray-900 border-gray-800 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">{getMovieName(movieId)}</h2>
                {Object.entries(branchesData).map(([branchId, dates]) => (
                  <div key={branchId} className="mb-6">
                    <h3 className="text-xl font-semibold text-[#FDB714] mb-3">{getBranchName(branchId)}</h3>
                    {Object.entries(dates).map(([date, times]) => (
                      <div key={date} className="mb-4 bg-gray-800 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-gray-300 font-medium">{date}</span>
                          <span className="text-gray-500 text-sm">{times.length} seans</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {times.sort((a, b) => a.show_time.localeCompare(b.show_time)).map((showtime) => (
                            <div
                              key={showtime._id}
                              className={`px-4 py-2 rounded-lg flex items-center gap-3 transition-all ${selectedShowtimes.includes(showtime._id) ? 'bg-blue-700 ring-2 ring-blue-500' : 'bg-gray-700'}`}
                            >
                              <input type="checkbox" checked={selectedShowtimes.includes(showtime._id)} onChange={() => toggleSelectShowtime(showtime._id)} className="w-4 h-4 cursor-pointer" />
                              <span className="text-white font-semibold">{showtime.show_time}</span>
                              {showtime.hall_number && <span className="text-xs bg-green-600 px-2 py-1 rounded text-white">{showtime.hall_number}</span>}
                              {showtime.format && <span className="text-xs bg-blue-600 px-2 py-1 rounded text-white">{showtime.format}</span>}
                              {showtime.language && <span className="text-xs bg-purple-600 px-2 py-1 rounded text-white">{showtime.language}</span>}
                              <button onClick={() => handleDelete(showtime._id)} className="text-red-400 hover:text-red-300 transition-colors ml-1" title="Sil">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Import Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">📊 Excel'den Seansları İçe Aktar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
              <h4 className="text-white font-semibold mb-2">📋 Beklenen Excel Formatı:</h4>
              <div className="text-gray-300 text-sm space-y-1">
                <p><strong>Sütunlar (tam olarak bu isimlerle):</strong></p>
                <ul className="list-disc list-inside ml-4">
                  <li><strong>Salon</strong>: Salon adı (örn: "Keçiören Cinefora")</li>
                  <li><strong>Film Adı</strong>: Film başlığı</li>
                  <li><strong>Salon Numarası</strong>: (örn: "SALON 4")</li>
                  <li><strong>Dil</strong>: (örn: "Türkçe Dublaj")</li>
                  <li><strong>Tarih</strong>: YYYY-MM-DD formatında</li>
                  <li><strong>Saatler</strong>: Virgülle ayrılmış (örn: "11:15,13:45")</li>
                </ul>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Dosya Seç (Excel veya CSV)</Label>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#FDB714] file:text-black hover:file:bg-[#e5a510] cursor-pointer" />
              {importFile && <p className="text-green-400 text-sm">✅ Seçilen dosya: {importFile.name}</p>}
            </div>
            <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-3">
              <p className="text-yellow-200 text-sm">⚠️ <strong>Not:</strong> Film ve salon adları veritabanındaki ile tam olarak eşleşmelidir.</p>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={() => { setShowImportModal(false); setImportFile(null); }} className="bg-gray-700 hover:bg-gray-600 text-white">İptal</Button>
            <Button onClick={handleImportSubmit} disabled={!importFile || importing} className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50">
              {importing ? '⏳ Yükleniyor...' : '📤 İçe Aktar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mapping Modal */}
      <Dialog open={showMappingModal} onOpenChange={setShowMappingModal}>
        <DialogContent className="bg-gray-900 border-gray-700 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">🔗 Film ve Salon Eşleştirme</DialogTitle>
          </DialogHeader>
          {validationResult && (
            <div className="space-y-4">
              <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
                <p className="text-gray-300 text-sm">📊 Toplam <strong>{validationResult.total_rows}</strong> satır bulundu. Aşağıdaki film ve salonlar sistemde bulunamadı:</p>
              </div>
              {validationResult.missing_movies && validationResult.missing_movies.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-white font-semibold">🎬 Bulunamayan Filmler</h4>
                  {validationResult.missing_movies.map((missingMovie) => (
                    <div key={missingMovie} className="bg-gray-800 rounded-lg p-3 space-y-2">
                      <Label className="text-gray-300">Excel'de: <span className="text-red-400 font-semibold">{missingMovie}</span></Label>
                      <select className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" value={movieMappings[missingMovie] || ''} onChange={(e) => setMovieMappings({ ...movieMappings, [missingMovie]: e.target.value })}>
                        <option value="">-- Sistemdeki film ile eşleştir --</option>
                        {validationResult.available_movies.map((movie) => <option key={movie.id} value={movie.id}>{movie.title}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              {validationResult.missing_branches && validationResult.missing_branches.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-white font-semibold">🏢 Bulunamayan Salonlar</h4>
                  {validationResult.missing_branches.map((missingBranch) => (
                    <div key={missingBranch} className="bg-gray-800 rounded-lg p-3 space-y-2">
                      <Label className="text-gray-300">Excel'de: <span className="text-red-400 font-semibold">{missingBranch}</span></Label>
                      <select className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white" value={branchMappings[missingBranch] || ''} onChange={(e) => setBranchMappings({ ...branchMappings, [missingBranch]: e.target.value })}>
                        <option value="">-- Sistemdeki salon ile eşleştir --</option>
                        {validationResult.available_branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={() => { setShowMappingModal(false); setValidationResult(null); setImportFile(null); }} className="bg-gray-700 hover:bg-gray-600 text-white">İptal</Button>
            <Button onClick={handleConfirmMapping} disabled={importing} className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50">
              {importing ? '⏳ İçe Aktarılıyor...' : '✅ Eşleştir ve İçe Aktar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </AdminLayout>
  );
};

export default AdminShowtimes;
